import {Component, OnInit, Input, ViewChild} from '@angular/core';
import {SpreadingTemplateService} from '../../../../../services/spreading-template.service';
import {TaxMappingLineItem} from '@models/tax-mapping-line-item';
import {INCOME_STATEMENT, DISPLAY_NAME_1065} from '@utils/constants';
import {AlertService} from '../../../../../services/alert.service';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {ConfirmationPopupComponent} from '@components/shared/popups/confirmation/confirmation-popup.component';
import {FileImportModalComponent} from '@components/shared/popups/file-import-modal/file-import-modal.component';
import {NgxPopupService} from '@components/shared/ngx-popups/ngx-popups/services/ngx-popup.service';

@Component({
    selector: 'app-edit-template-mappings',
    templateUrl: './edit-template-mappings.component.html',
    styleUrls: ['./edit-template-mappings.component.scss'],
    standalone: false
})
export class EditTemplateMappingsComponent implements OnInit {
  @ViewChild('autosize') autosize: CdkTextareaAutosize;
  @Input() templateId = null;
  @Input() templateName = '';
  @Input() incomeStatementTaxonomyItems = null;
  @Input() balanceSheetTaxonomyItems = null;

  private static readonly IMPORT_DESCRIPTION = 'Upload a CSV file containing REF to Tax Form Mappings. A file can be created manually, or by isolating the relevant columns in an Excel spreadsheet and \'saving as\' a CSV file. Do not include any header rows or extraneous data as all rows in the file will be interpreted as mapping definitions.';
  private static readonly IMPORT_WARNING = 'Note: this will overwrite all tax form mappings for this template.';
  private static readonly MAX_FILE_SIZE = 10 * 1024 * 1024;

  readonly INVALID_MAPPING_SENTINEL_ID = -1;

  saveButtonDisabled = true;
  selectedTaxFormItems = [];
  selectedTaxFormType = DISPLAY_NAME_1065;
  savedTaxFormMappings: { [taxFormType: string]: Array<TaxMappingLineItem>; } = {};
  newOverrides: { [taxFormType: string]: Array<TaxMappingLineItem>; } = {};
  isImporting = false;
  mappingErrorCount = 0;

  readonly nullTaxonomyItemOption = {
    label: 'Unmapped',
    standardLineItemId: 0,
    descriptionEditMode: false,
    originalNoteValue: null
  };

  readonly selectPlaceholderOption = {
    label: 'Select',
    standardLineItemId: this.INVALID_MAPPING_SENTINEL_ID,
    descriptionEditMode: false,
    originalNoteValue: null
  };
  invertedValueOptions: { label: string; value: boolean }[] = [
    { label: 'True', value: true },
    { label: 'False', value: false }
  ];
  readonly INCOME_STATEMENT = INCOME_STATEMENT;

  constructor(
    private _alertService: AlertService,
    private _spreadingTemplateService: SpreadingTemplateService,
    private _popupService: NgxPopupService
  ) {
  }

  ngOnInit(): void {
    this.mappingErrorCount = 0;
    this.saveButtonDisabled = true;
    this._spreadingTemplateService.getTaxFormMappingsForSpreadingTemplate(this.templateId).subscribe(data => {
      this.savedTaxFormMappings = data;
      Object.entries(this.savedTaxFormMappings).forEach(([taxFormType, taxFormItems]) => {
        for (const lineItem of taxFormItems) {
          if (lineItem.taxonomyItemIdOverride === this.INVALID_MAPPING_SENTINEL_ID) {
            lineItem.hasMappingError = true;
            lineItem.currentStateOfOverrideIdToBeSaved = this.INVALID_MAPPING_SENTINEL_ID;
            lineItem.mappingOptions = [this.selectPlaceholderOption];
            this.mappingErrorCount++;
          } else {
            const mappedTaxonomyItem = this.retrieveTaxonomyItemById(lineItem);
            if (mappedTaxonomyItem !== undefined) {
              lineItem.mappedTaxonomyItemLabel = mappedTaxonomyItem.label;
              lineItem.mappingOptions = lineItem.defaultTaxonomyItemId === 0 ? [this.nullTaxonomyItemOption] : [];
              lineItem.mappingOptions.push(mappedTaxonomyItem);
            } else {
              lineItem.mappingOptions = [this.nullTaxonomyItemOption];
            }
            lineItem.currentStateOfOverrideIdToBeSaved = lineItem.taxonomyItemIdOverride;
          }
          lineItem.currentStateOfOverrideInversionToBeSaved = lineItem.overrideInvertedValue;
          lineItem.descriptionEditMode = false;
          lineItem.originalNoteValue = lineItem.note;
        }
      });
      this.changeTaxForm(this.selectedTaxFormType);
      this.newOverrides = {};
    });
  }

  retrieveTaxonomyItemById(lineItem: TaxMappingLineItem) {
    for (const taxonomyItem of lineItem.statementType === INCOME_STATEMENT ? this.incomeStatementTaxonomyItems : this.balanceSheetTaxonomyItems) {
      if (lineItem.taxonomyItemIdOverride === null && taxonomyItem.standardLineItemId === lineItem.defaultTaxonomyItemId) {
        return taxonomyItem;
      } else if (lineItem.taxonomyItemIdOverride !== null && taxonomyItem.standardLineItemId === lineItem.taxonomyItemIdOverride) {
        return taxonomyItem;
      }
    }
  }

  changeTaxForm(selectedTaxFormType: string) {
    this.selectedTaxFormType = selectedTaxFormType;
    this.selectedTaxFormItems = this.savedTaxFormMappings[this.selectedTaxFormType];
  }

  setMappingOptionsForLineItem(lineItem: TaxMappingLineItem) {
    const initialMappingOptions = lineItem.hasMappingError
      ? [this.selectPlaceholderOption, this.nullTaxonomyItemOption]
      : [this.nullTaxonomyItemOption];
    return lineItem.statementType === INCOME_STATEMENT
      ? initialMappingOptions.concat(this.incomeStatementTaxonomyItems)
      : initialMappingOptions.concat(this.balanceSheetTaxonomyItems);
  }

  setOverrideForTaxLineItem(selectedOverrideId: any, selectedLineItem: TaxMappingLineItem) {
    const numericId = selectedOverrideId === null ? null : Number(selectedOverrideId);
    if (numericId === this.INVALID_MAPPING_SENTINEL_ID) {
      return;
    }
    if (selectedLineItem.hasMappingError) {
      selectedLineItem.hasMappingError = false;
      this.mappingErrorCount = Math.max(0, this.mappingErrorCount - 1);
    }
    if (numericId === null || numericId === selectedLineItem.defaultTaxonomyItemId) {
      selectedOverrideId = null;
    } else {
      selectedOverrideId = numericId;
    }
    const overrideInvertFlag =
    selectedLineItem.overrideInvertedValue ?? selectedLineItem.defaultInvertFlag;
    this.savedTaxFormMappings[this.selectedTaxFormType].forEach((item) => {
      if (item.lineItemName === selectedLineItem.lineItemName) {
        const itemFromNewOverrides = this.retrieveLineItemFromNewOverrides(item.lineItemName);
        if (itemFromNewOverrides === undefined) {
          if (selectedOverrideId !== item.taxonomyItemIdOverride) {
            this.addItemToNewOverrides(item, selectedOverrideId, overrideInvertFlag)
          }
        } else if ((selectedOverrideId !== item.taxonomyItemIdOverride && selectedOverrideId !== itemFromNewOverrides.taxonomyItemIdOverride) || (selectedLineItem.overrideInvertedValue !== itemFromNewOverrides.overrideInvertedValue)) {
          this.updateOverrideIdForItemInNewOverrides(selectedOverrideId, itemFromNewOverrides.overrideInvertedValue, item, itemFromNewOverrides);
        } else if (selectedOverrideId === item.taxonomyItemIdOverride) {
          if (selectedLineItem.note === selectedLineItem.originalNoteValue) {
            this.removeItemFromNewOverrides(item);
          }
        }
      }
    });
  }

  setOverrideForTaxLineItemInversion(selectedInversionFlag: any, selectedLineItem: TaxMappingLineItem) {
    selectedInversionFlag = selectedInversionFlag === null || selectedInversionFlag === selectedLineItem.defaultInvertFlag ? null : selectedInversionFlag;
    this.savedTaxFormMappings[this.selectedTaxFormType].forEach((item) => {
      if (item.lineItemName === selectedLineItem.lineItemName) {
        const itemFromNewOverrides = this.retrieveLineItemFromNewOverrides(item.lineItemName);
        if (itemFromNewOverrides === undefined) {
          if (selectedInversionFlag !== item.overrideInvertedValue) {
            this.addItemToNewOverrides(item, selectedLineItem.taxonomyItemIdOverride, selectedInversionFlag)
          }
        } else if ((selectedInversionFlag !== item.overrideInvertedValue && selectedInversionFlag !== itemFromNewOverrides.overrideInvertedValue) || (selectedLineItem.taxonomyItemIdOverride !== itemFromNewOverrides.taxonomyItemIdOverride)) {
          this.updateOverrideIdForItemInNewOverrides(itemFromNewOverrides.taxonomyItemIdOverride, selectedInversionFlag, item, itemFromNewOverrides);
        } else if (selectedInversionFlag === item.overrideInvertedValue) {
          if (selectedLineItem.note === selectedLineItem.originalNoteValue) {
            this.removeItemFromNewOverrides(item);
          }
        }
      }
    });
  }

  retrieveLineItemFromNewOverrides(lineItemName) {
    if (this.selectedTaxFormType in this.newOverrides) {
      for (const lineItem of Array.from(this.newOverrides[this.selectedTaxFormType])) {
        if (lineItem.lineItemName === lineItemName) {
          return lineItem;
        }
      }
    }
  }

  addItemToNewOverrides(item: TaxMappingLineItem, selectedOverrideId: any, selectedInversionFlag: any) {
    if (!(this.selectedTaxFormType in this.newOverrides)) {
      this.newOverrides[this.selectedTaxFormType] = [];
    }
    const newOverrideItem = JSON.parse(JSON.stringify(item)) as TaxMappingLineItem;
    newOverrideItem.taxonomyItemIdOverride = selectedOverrideId;
    newOverrideItem.overrideInvertedValue = selectedInversionFlag;
    newOverrideItem.mappedTaxonomyItemLabel = undefined;
    newOverrideItem.currentStateOfOverrideIdToBeSaved = undefined;
    newOverrideItem.currentStateOfOverrideInversionToBeSaved = undefined;
    newOverrideItem.defaultInvertFlag = undefined;
    newOverrideItem.mappingOptions = undefined;
    newOverrideItem.defaultTaxonomyItemId = undefined;
    newOverrideItem.originalNoteValue = undefined;
    newOverrideItem.descriptionEditMode = undefined;
    newOverrideItem.hasMappingError = undefined;
    this.newOverrides[this.selectedTaxFormType].push(newOverrideItem);
    this.saveButtonDisabled = false;
    item.currentStateOfOverrideIdToBeSaved = selectedOverrideId;
    item.currentStateOfOverrideInversionToBeSaved = selectedInversionFlag;
  }

  updateOverrideIdForItemInNewOverrides(selectedOverrideId: any, selectedInversionFlag: any, item: TaxMappingLineItem, itemFromNewOverrides: TaxMappingLineItem) {
    itemFromNewOverrides.taxonomyItemIdOverride = selectedOverrideId;
    itemFromNewOverrides.overrideInvertedValue = selectedInversionFlag;
    item.currentStateOfOverrideIdToBeSaved = selectedOverrideId;
    item.currentStateOfOverrideInversionToBeSaved = selectedInversionFlag;
    this.saveButtonDisabled = false;
  }

  removeItemFromNewOverrides(item: TaxMappingLineItem) {
    this.newOverrides[this.selectedTaxFormType] = Array.from(this.newOverrides[this.selectedTaxFormType]).filter(function (overrideItem) {
      return overrideItem.lineItemName !== item.lineItemName;
    });
    item.currentStateOfOverrideIdToBeSaved = null;
    item.currentStateOfOverrideInversionToBeSaved = null;
    if (this.newOverrides[this.selectedTaxFormType].length === 0) {
      delete this.newOverrides[this.selectedTaxFormType];
      if (Object.keys(this.newOverrides).length === 0) {
        this.saveButtonDisabled = true;
      }
    }
  }

  saveTaxFormMappingOverrides() {
    if (this.mappingErrorCount > 0) {
      this._alertService.error(
        `Please resolve all mapping errors (${this.mappingErrorCount} remaining) before saving.`
      );
      return;
    }
    const data = {
      'taxFormMappingOverrides': this.newOverrides,
    };
    this._spreadingTemplateService.updateTaxFormMappingOverrides(this.templateId, data).subscribe(result => {
      this._alertService.success('Saved Successfully! It may take a moment for your data to update');
      this.saveButtonDisabled = true;
      this.ngOnInit();
    }, error => {
      this._alertService.error(error.message);
    });
  }

  downloadTaxFormMappingsToExcel() {
    this._spreadingTemplateService.downloadTaxFormMappingsToExcel(this.templateId, this.templateName)
  }

  launchRestoreAllMappingsModal() {
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: 'RESTORE ALL DEFAULTS?',
        text: 'Are you sure you want to restore all mappings to their default values? This action cannot be undone.',
        confirmButtonText: 'Yes'
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            this.updateAllTaxFormMappingsToDefaultForTaxForm()
          }
        }
      },
    });
  }

  updateAllTaxFormMappingsToDefaultForTaxForm() {
    this.savedTaxFormMappings[this.selectedTaxFormType].forEach((item) => {
      if (item.hasMappingError) {
        item.hasMappingError = false;
        this.mappingErrorCount = Math.max(0, this.mappingErrorCount - 1);
      }
      // 2 places where the items can occur, in saved mappings or new overrides, or both
      const itemFromNewOverrides = this.retrieveLineItemFromNewOverrides(item.lineItemName);
      if ((item.taxonomyItemIdOverride != null || item.overrideInvertedValue != null) && itemFromNewOverrides === undefined) {
        // item came from db and has not been edited, need to add it to overrides to save
        this.addItemToNewOverrides(item, null, null)
        this.setMappingOptionsForLineItem(item);
        return
      }
      if (itemFromNewOverrides === undefined) {
        // if the mapping doesn't exit in the new overrides line item doesn't have any associated overrides
        return
      }
      if ((item.taxonomyItemIdOverride != null || item.overrideInvertedValue != null) && (itemFromNewOverrides.taxonomyItemIdOverride != null || itemFromNewOverrides.overrideInvertedValue != null)) {
        // item came from the db and has been edited to a non-default mapping but not saved
        this.updateOverrideIdForItemInNewOverrides(null, null, item, itemFromNewOverrides);
      } else if (itemFromNewOverrides.taxonomyItemIdOverride != null || itemFromNewOverrides.overrideInvertedValue != null) {
        // value has been only edited and not saved, so it only exists in new overrides
        this.removeItemFromNewOverrides(item);
      }
      this.setMappingOptionsForLineItem(item);
    });
  }

  switchLineItemNoteEditMode(item: TaxMappingLineItem) {
    item.descriptionEditMode = !item.descriptionEditMode
  }

  setNewNoteTaxLineItem(selectedLineItem: TaxMappingLineItem) {
    if (selectedLineItem.note.trim() === '') {
      selectedLineItem.note = null
    }

    const itemFromNewOverrides = this.retrieveLineItemFromNewOverrides(selectedLineItem.lineItemName);
    const newNoteValue = selectedLineItem.note
    if (itemFromNewOverrides === undefined) {
      if (newNoteValue !== selectedLineItem.originalNoteValue) {
        this.addItemToNewOverrides(selectedLineItem, selectedLineItem.taxonomyItemIdOverride, selectedLineItem.overrideInvertedValue)
      }
    } else if (itemFromNewOverrides.taxonomyItemIdOverride != null || itemFromNewOverrides.overrideInvertedValue != null) {
      itemFromNewOverrides.note = newNoteValue;
      this.saveButtonDisabled = false;
    } else if (itemFromNewOverrides.taxonomyItemIdOverride == null && itemFromNewOverrides.overrideInvertedValue == null) {
      if (!newNoteValue || (selectedLineItem.originalNoteValue === newNoteValue)) {
        this.removeItemFromNewOverrides(selectedLineItem)
      } else {
        itemFromNewOverrides.note = newNoteValue;
        this.saveButtonDisabled = false;
      }
    }
  }

  importMappings(): void {
    if (this.hasAnyMappingOverrides()) {
      this._popupService.open({
        componentType: ConfirmationPopupComponent,
        cssClass: 'modal-confirmation',
        inputs: {
          question: 'IMPORT TAX FORM MAPPINGS?',
          text: 'This will override all existing tax form mapping overrides. Are you sure you want to import?',
          confirmButtonText: 'Yes, Import'
        },
        outputs: {
          callback: (confirmed: boolean) => {
            if (confirmed) {
              this.openImportModal();
            }
          }
        }
      });
    } else {
      this.openImportModal();
    }
  }

  exportMappings(): void {
    if (!this.savedTaxFormMappings || Object.keys(this.savedTaxFormMappings).length === 0) {
      this._alertService.warning('No tax form mappings to export.');
      return;
    }

    try {
      const csvContent = this.generateCSVContent();
      const fileName = this.getSanitizedFileName(this.templateName) + '_tax_form_mappings.csv';
      this.downloadCSV(csvContent, fileName);
      this._alertService.success('Tax form mappings exported successfully!');
    } catch (error) {
      this._alertService.error('Export failed. Please try again.');
    }
  }

  private hasAnyMappingOverrides(): boolean {
    return Object.values(this.savedTaxFormMappings).some(items =>
      items.some(item => item.taxonomyItemIdOverride != null || item.overrideInvertedValue != null)
    );
  }

  private openImportModal(): void {
    this._popupService.open({
      componentType: FileImportModalComponent,
      cssClass: 'modal-file-import',
      inputs: {
        title: 'UPLOAD TAX FORM MAPPING FILE',
        description: EditTemplateMappingsComponent.IMPORT_DESCRIPTION,
        noteText: EditTemplateMappingsComponent.IMPORT_WARNING,
        acceptedFileTypes: '.csv'
      },
      outputs: {
        fileSelected: (file: File) => this.processImportFile(file)
      }
    });
  }

  private processImportFile(file: File): void {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this._alertService.error('Please select a valid CSV file.');
      return;
    }
    if (file.size === 0) {
      this._alertService.error('The selected file is empty.');
      return;
    }
    if (file.size > EditTemplateMappingsComponent.MAX_FILE_SIZE) {
      this._alertService.error('File size exceeds the maximum allowed size (10MB).');
      return;
    }

    this.isImporting = true;
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const content = e.target?.result as string;
      if (!content?.trim()) {
        this._alertService.error('The selected file is empty.');
        this.isImporting = false;
        return;
      }

      this._spreadingTemplateService.importTaxFormMappingsFromCSV(this.templateId, content, true).subscribe(
        () => {
          this._popupService.closeAll();
          this.isImporting = false;
          this._alertService.success('Tax form mappings imported successfully!');
          this.ngOnInit();
        },
        (error) => {
          const rawMsg = error?.error?.message || error?.message || 'An unknown error occurred';
          const firstError = rawMsg.split('\n')[0];
          this._alertService.error('Import failed: ' + firstError);
          this.isImporting = false;
        }
      );
    };

    reader.onerror = () => {
      this._alertService.error('Import failed: Failed to read the file.');
      this.isImporting = false;
    };

    reader.readAsText(file);
  }

  private generateCSVContent(): string {
    const rows: string[] = [];

    for (const [taxFormType, lineItems] of Object.entries(this.savedTaxFormMappings)) {
      for (const item of lineItems) {
        const invertValue = item.overrideInvertedValue !== null ? item.overrideInvertedValue : item.defaultInvertFlag;
        const statementTypeLabel = item.statementType === INCOME_STATEMENT
          ? 'Income Statement' : 'Balance Sheet';
        rows.push([
          this.escapeCSVValue(taxFormType),
          this.escapeCSVValue(item.lineItemName),
          this.escapeCSVValue(this.getTaxonomyLabelById(item)),
          invertValue ? 'True' : 'False',
          statementTypeLabel,
          this.escapeCSVValue(item.note || '')
        ].join(','));
      }
    }

    return rows.join('\n');
  }

  private getTaxonomyLabelById(lineItem: TaxMappingLineItem): string {
    const taxonomyId = lineItem.currentStateOfOverrideIdToBeSaved !== null
      ? lineItem.currentStateOfOverrideIdToBeSaved
      : (lineItem.taxonomyItemIdOverride ?? lineItem.defaultTaxonomyItemId);

    if (!taxonomyId || taxonomyId === this.INVALID_MAPPING_SENTINEL_ID) {
      return 'Unmapped';
    }

    const taxonomyItems = lineItem.statementType === INCOME_STATEMENT
      ? this.incomeStatementTaxonomyItems
      : this.balanceSheetTaxonomyItems;

    const match = taxonomyItems?.find(item => item.standardLineItemId === taxonomyId);
    return match ? match.label : 'Unmapped';
  }

  private escapeCSVValue(value: string): string {
    if (value == null) {
      return '';
    }
    const str = String(value);
    return (str.includes(',') || str.includes('"') || str.includes('\n'))
      ? '"' + str.replace(/"/g, '""') + '"'
      : str;
  }

  private downloadCSV(content: string, fileName: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private getSanitizedFileName(name: string): string {
    return (name || 'template').replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
  }
}
